import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Gavel, 
  Plus, 
  Search, 
  FileText, 
  Calendar, 
  Trash2, 
  Play, 
  ChevronRight, 
  DollarSign, 
  User, 
  Building,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '../config';

export default function VertenzaPage() {
  const navigate = useNavigate();
  const [pratiche, setPratiche] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('tutti');
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [ccnlList, setCcnlList] = useState([]);
  const [newPratica, setNewPratica] = useState({
    numero: '',
    titolo: '',
    lavoratoreNome: '',
    lavoratoreCf: '',
    datoreNome: '',
    datorePiva: '',
    ccnlCodice: 'commercio-confcommercio',
    ccnlNome: 'CCNL Commercio - Confcommercio',
    livello: '',
    livelloApplicato: '',
    dataAssunzione: '',
    dataCessazione: '',
    periodoDa: '2024-01',
    periodoA: '2024-06',
    note: '',
    propostaTransazione: 0,
    regioneResidenza: 'Lombardia',
    comuneResidenza: '',
    addizionaleRegionalePct: 1.73,
    addizionaleComunalePct: 0.8
  });

  useEffect(() => {
    fetchPratiche();
    fetchCcnlList();
  }, []);

  const fetchCcnlList = async () => {
    try {
      const response = await axios.get(`${API_URL}/sindacato/ccnl`);
      setCcnlList(response.data.ccnl || []);
    } catch (err) {
      console.error('Errore nel caricamento dei CCNL:', err);
    }
  };

  const fetchPratiche = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/sindacato/vertenza`);
      setPratiche(response.data);
    } catch (err) {
      console.error(err);
      setError('Impossibile caricare le pratiche di vertenza.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePratica = async (e) => {
    e.preventDefault();
    if (!newPratica.titolo || !newPratica.lavoratoreNome || !newPratica.datoreNome) {
      toast.error('Compila i campi obbligatori (Titolo, Lavoratore, Datore).');
      return;
    }
    
    // Auto generate a practice number if not set
    const numero_pratica = newPratica.numero || `V-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
    const payload = { ...newPratica, numero: numero_pratica };

    try {
      const response = await axios.post(`${API_URL}/sindacato/vertenza`, payload);
      toast.success('Pratica di vertenza creata con successo.');
      setShowCreateModal(false);
      // Reset form
      setNewPratica({
        numero: '',
        titolo: '',
        lavoratoreNome: '',
        lavoratoreCf: '',
        datoreNome: '',
        datorePiva: '',
        ccnlCodice: 'commercio-confcommercio',
        ccnlNome: 'CCNL Commercio - Confcommercio',
        livello: '',
        livelloApplicato: '',
        dataAssunzione: '',
        dataCessazione: '',
        periodoDa: '2024-01',
        periodoA: '2024-06',
        note: '',
        propostaTransazione: 0,
        regioneResidenza: 'Lombardia',
        comuneResidenza: '',
        addizionaleRegionalePct: 1.73,
        addizionaleComunalePct: 0.8
      });
      // Redirect to detail page
      navigate(`/vertenza/${response.data.id}`);
    } catch (err) {
      console.error(err);
      toast.error("Errore durante la creazione della pratica.");
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!window.confirm('Sei sicuro di voler eliminare questa pratica? L\'operazione è irreversibile.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/sindacato/vertenza/${id}`);
      toast.success('Pratica eliminata.');
      fetchPratiche();
    } catch (err) {
      console.error(err);
      toast.error('Errore durante l\'eliminazione.');
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val);
  };

  const filteredPratiche = pratiche.filter(p => {
    const matchesSearch = 
      p.titolo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.lavoratoreNome.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.datoreNome.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.numero.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesStatus = statusFilter === 'tutti' || p.stato === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      bozza: 'bg-slate-100 text-slate-800 border-slate-200',
      istruttoria: 'bg-amber-100 text-amber-800 border-amber-200',
      calcolata: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      trattativa: 'bg-blue-100 text-blue-800 border-blue-200',
      conciliazione: 'bg-purple-100 text-purple-800 border-purple-200',
      chiusa: 'bg-red-100 text-red-800 border-red-200'
    };
    return (
      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${styles[status] || styles.bozza}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  // Stats calculations
  const totalCredito = pratiche.reduce((acc, curr) => acc + (curr.risultato?.totaleCredito || 0), 0);
  const praticheCalcolate = pratiche.filter(p => p.risultato).length;
  const praticheAperte = pratiche.filter(p => p.stato !== 'chiusa').length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="spinner animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 p-1">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
              <Gavel className="w-8 h-8 text-forest" />
              Pratiche di Vertenza
            </h1>
            <p className="text-muted-foreground mt-1">
              Calcoli differenze retributive (art. 429), TFR (art. 2120), e simulazioni conciliative.
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="bg-forest hover:bg-forest/90 text-white font-medium shadow-sm transition">
            <Plus className="w-4 h-4 mr-2" />
            Nuova Pratica Vertenza
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border border-slate-200 shadow-sm bg-gradient-to-tr from-white to-slate-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Pratiche Attive</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-800">{praticheAperte}</div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 shadow-sm bg-gradient-to-tr from-white to-slate-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Calcoli Elaborati</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-800">{praticheCalcolate}</div>
            </CardContent>
          </Card>
          <Card className="border border-forest/20 shadow-sm bg-gradient-to-tr from-forest/5 to-emerald-50/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-forest">Credito Stimato Totale</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-forest">{formatCurrency(totalCredito)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center bg-white p-4 rounded-lg border border-slate-200 shadow-xs">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Cerca per lavoratore, datore, numero..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 w-full border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-forest focus:border-forest"
            />
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <span className="text-xs text-muted-foreground font-medium">Stato:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-200 rounded-md py-1.5 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-forest bg-white"
            >
              <option value="tutti">Tutti</option>
              <option value="bozza">Bozza</option>
              <option value="istruttoria">Istruttoria</option>
              <option value="calcolata">Calcolata</option>
              <option value="trattativa">Trattativa</option>
              <option value="conciliazione">Conciliazione</option>
              <option value="chiusa">Chiusa</option>
            </select>
          </div>
        </div>

        {/* Practice List */}
        {filteredPratiche.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-lg bg-slate-50/50">
            <Gavel className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-800">Nessuna Pratica di Vertenza</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-sm mx-auto">
              Crea una nuova pratica manuale oppure converti i cedolini dall'area consulenza del lavoratore.
            </p>
            <Button onClick={() => setShowCreateModal(true)} className="bg-forest mt-4 text-white text-sm hover:bg-forest/90">
              Crea Nuova Pratica
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Pratica</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Lavoratore</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Datore</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Stato</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Credito Stimato</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {filteredPratiche.map((p) => (
                  <tr 
                    key={p.id} 
                    className="hover:bg-slate-50/65 cursor-pointer transition"
                    onClick={() => navigate(`/vertenza/${p.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-mono text-sm font-bold text-forest">{p.numero}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-xs">{p.titolo}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        {p.lavoratoreNome}
                      </div>
                      <div className="text-xs text-muted-foreground">{p.lavoratoreCf || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                        <Building className="w-3.5 h-3.5 text-slate-400" />
                        {p.datoreNome}
                      </div>
                      <div className="text-xs text-muted-foreground">{p.datorePiva || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(p.stato)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold tabular-nums text-slate-900">
                      {formatCurrency(p.risultato?.totaleCredito || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end items-center gap-2">
                        <button 
                          onClick={(e) => handleDelete(p.id, e)}
                          className="p-1.5 text-slate-400 hover:text-red-600 transition"
                          title="Elimina pratica"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <ChevronRight className="w-5 h-5 text-slate-300" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-2xl w-full p-6 space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-forest" />
                  Crea Nuova Pratica Vertenza
                </h3>
                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600 font-bold text-lg">×</button>
              </div>
              
              <form onSubmit={handleCreatePratica} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">Titolo Pratica *</label>
                    <input
                      type="text"
                      required
                      placeholder="es. Vertenza Rossi vs Acme Srl"
                      value={newPratica.titolo}
                      onChange={(e) => setNewPratica({ ...newPratica, titolo: e.target.value })}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">Codice Pratica (Opzionale)</label>
                    <input
                      type="text"
                      placeholder="es. V-2026-01"
                      value={newPratica.numero}
                      onChange={(e) => setNewPratica({ ...newPratica, numero: e.target.value })}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">Lavoratore (Nome Completo) *</label>
                    <input
                      type="text"
                      required
                      placeholder="es. Mario Rossi"
                      value={newPratica.lavoratoreNome}
                      onChange={(e) => setNewPratica({ ...newPratica, lavoratoreNome: e.target.value })}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">Codice Fiscale Lavoratore</label>
                    <input
                      type="text"
                      placeholder="es. RSSMRA80A01H501U"
                      value={newPratica.lavoratoreCf}
                      onChange={(e) => setNewPratica({ ...newPratica, lavoratoreCf: e.target.value.toUpperCase() })}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">Datore di Lavoro *</label>
                    <input
                      type="text"
                      required
                      placeholder="es. Acme Srl"
                      value={newPratica.datoreNome}
                      onChange={(e) => setNewPratica({ ...newPratica, datoreNome: e.target.value })}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">P. IVA / C.F. Datore</label>
                    <input
                      type="text"
                      placeholder="es. 01234567890"
                      value={newPratica.datorePiva}
                      onChange={(e) => setNewPratica({ ...newPratica, datorePiva: e.target.value })}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">CCNL Riferimento</label>
                    <select
                      value={newPratica.ccnlCodice}
                      onChange={(e) => {
                        const selected = ccnlList.find(c => c.id === e.target.value);
                        if (selected) {
                          setNewPratica({ 
                            ...newPratica, 
                            ccnlCodice: selected.id,
                            ccnlNome: selected.nome
                          });
                        }
                      }}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest bg-white"
                    >
                      {ccnlList.length === 0 ? (
                        <>
                          <option value="commercio-confcommercio">CCNL Commercio - Confcommercio</option>
                          <option value="metalmeccanici-industria">CCNL Metalmeccanici - Federmeccanica</option>
                          <option value="turismo-pubblici-esercizi">CCNL Turismo - Pubblici Esercizi</option>
                          <option value="edilizia-industria">CCNL Edilizia - Industria</option>
                          <option value="logistica-trasporto-merci">CCNL Trasporto Merci e Logistica</option>
                          <option value="chimico-farmaceutico">CCNL Chimico Farmaceutico</option>
                          <option value="chimica-industria">CCNL Chimica - Industria</option>
                          <option value="chimici-pmi-confapi">CCNL Chimici PMI - Confapi</option>
                        </>
                      ) : (
                        ccnlList.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nome}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-slate-600">Liv. Richiesto</label>
                      <input
                        type="text"
                        placeholder="es. 4"
                        value={newPratica.livello}
                        onChange={(e) => setNewPratica({ ...newPratica, livello: e.target.value })}
                        className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-slate-600">Liv. Applicato</label>
                      <input
                        type="text"
                        placeholder="es. 5"
                        value={newPratica.livelloApplicato}
                        onChange={(e) => setNewPratica({ ...newPratica, livelloApplicato: e.target.value })}
                        className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">Periodo Inizio (YYYY-MM) *</label>
                    <input
                      type="text"
                      required
                      placeholder="es. 2024-01"
                      value={newPratica.periodoDa}
                      onChange={(e) => setNewPratica({ ...newPratica, periodoDa: e.target.value })}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">Periodo Fine (YYYY-MM) *</label>
                    <input
                      type="text"
                      required
                      placeholder="es. 2024-06"
                      value={newPratica.periodoA}
                      onChange={(e) => setNewPratica({ ...newPratica, periodoA: e.target.value })}
                      className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest font-mono"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 border-t pt-3">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" className="bg-forest hover:bg-forest/90 text-white font-medium">
                    Salva e Genera Mesi
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
